/**
 * Shared overlay for PB Unified Navigation dropdowns (brand switcher, mega menus).
 * Shows a full-screen dimmed backdrop; nav bar and active panel stay above it.
 */
(function () {
  const NAV_SELECTOR = '[data-pb-unified-nav]';
  const BACKDROP_SELECTOR = '[data-pb-unified-nav-backdrop]';
  const BAR_SELECTOR = '.pb-unified-nav__bar';
  const DEFAULT_DROPDOWN_GAP = 20;
  const openLayers = new Set();
  let scrollLockState = null;

  function getNavRoot() {
    return document.querySelector(NAV_SELECTOR);
  }

  function getBackdrop() {
    return getNavRoot()?.querySelector(BACKDROP_SELECTOR);
  }

  function getNavBar() {
    return getNavRoot()?.querySelector(BAR_SELECTOR);
  }

  function updateBrandSwitcherPanelBounds() {
    const root = getNavRoot();
    const navBar = getNavBar();
    if (!root || !navBar) return;

    const isMobile = window.matchMedia('(max-width: 992px)').matches;
    const trigger = root.querySelector(
      isMobile
        ? '.pb-unified-nav-brand-switcher--mobile .pb-unified-nav-brand-switcher__trigger'
        : '.pb-unified-nav-brand-switcher--desktop .pb-unified-nav-brand-switcher__trigger'
    );

    if (!trigger) return;

    if (isMobile) {
      root.style.removeProperty('--pb-unified-nav-brand-panel-left');
      root.style.removeProperty('--pb-unified-nav-brand-panel-right');
      return;
    }

    const barRect = navBar.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    root.style.setProperty('--pb-unified-nav-brand-panel-left', `${triggerRect.left}px`);
    root.style.setProperty(
      '--pb-unified-nav-brand-panel-right',
      `${Math.max(0, window.innerWidth - barRect.right)}px`
    );
  }

  function getDropdownGap(root) {
    const value = getComputedStyle(root).getPropertyValue('--pb-unified-nav-dropdown-gap').trim();
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : DEFAULT_DROPDOWN_GAP;
  }

  function updateDropdownTop() {
    const root = getNavRoot();
    const navBar = getNavBar();
    if (!root || !navBar) return;

    const bottom = navBar.getBoundingClientRect().bottom;
    const gap = getDropdownGap(root);
    root.style.setProperty('--pb-unified-nav-dropdown-top', `${bottom + gap}px`);
    updateBrandSwitcherPanelBounds();
  }

  function lockPageScroll() {
    if (scrollLockState) return;

    const html = document.documentElement;
    const body = document.body;
    const scrollbarComp = Math.max(0, window.innerWidth - html.clientWidth);

    scrollLockState = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    if (scrollbarComp > 0) {
      body.style.paddingRight = `${scrollbarComp}px`;
    }
  }

  function unlockPageScroll() {
    if (!scrollLockState) return;

    const html = document.documentElement;
    const body = document.body;

    html.style.overflow = scrollLockState.htmlOverflow;
    body.style.overflow = scrollLockState.bodyOverflow;
    body.style.paddingRight = scrollLockState.bodyPaddingRight;
    scrollLockState = null;
  }

  function syncBackdrop() {
    const root = getNavRoot();
    const backdrop = getBackdrop();
    if (!root || !backdrop) return;

    const isOpen = openLayers.size > 0;

    if (isOpen) {
      backdrop.removeAttribute('hidden');
      root.classList.add('pb-unified-nav--overlay-open');
      document.documentElement.classList.add('pb-unified-nav-overlay-open');
      lockPageScroll();
      updateDropdownTop();
    } else {
      backdrop.setAttribute('hidden', '');
      root.classList.remove('pb-unified-nav--overlay-open');
      document.documentElement.classList.remove('pb-unified-nav-overlay-open');
      unlockPageScroll();
    }
  }

  function open(layerId) {
    if (!layerId) return;
    openLayers.add(layerId);
    syncBackdrop();
  }

  function close(layerId) {
    if (!layerId) return;
    openLayers.delete(layerId);
    syncBackdrop();
  }

  function closeAll() {
    document.querySelectorAll('[data-pb-unified-nav-mega-menu]').forEach((panel) => {
      panel.setAttribute('hidden', '');
      const trigger = document.querySelector(`[aria-controls="${panel.id}"]`);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    document.querySelectorAll('pb-unified-nav-brand-switcher').forEach((el) => {
      const panel = document.getElementById(el.dataset.panelId);
      const button = el.querySelector('.pb-unified-nav-brand-switcher__trigger');
      if (panel) panel.setAttribute('hidden', '');
      if (button) button.setAttribute('aria-expanded', 'false');
    });

    document.querySelectorAll('.pb-unified-nav__mobile-menu').forEach((panel) => {
      panel.setAttribute('hidden', '');
      // Default bar + convert A/B bar both control the same panel — reset every trigger.
      document.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        if (trigger.dataset.menuLabel) {
          trigger.setAttribute('aria-label', trigger.dataset.menuLabel);
        }
      });
    });

    document.querySelectorAll('[data-pb-unified-nav-search]').forEach((panel) => {
      panel.setAttribute('hidden', '');
    });

    document.querySelectorAll('[data-open-search-popup]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    openLayers.clear();
    syncBackdrop();
  }

  function isOpen(layerId) {
    return openLayers.has(layerId);
  }

  function init() {
    const root = getNavRoot();
    const backdrop = getBackdrop();
    if (!root || !backdrop) return;

    backdrop.addEventListener('click', closeAll);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openLayers.size > 0) {
        closeAll();
      }
    });

    window.addEventListener('scroll', updateDropdownTop, { passive: true });
    window.addEventListener('resize', updateDropdownTop, { passive: true });
    updateDropdownTop();
  }

  window.PBUnifiedNavOverlay = {
    open,
    close,
    closeAll,
    isOpen,
    updateDropdownTop,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
